import type { RouteRecordRaw } from "vue-router"
import type { RoleType } from "@/api/login/types/login"
import type { OrganizationCapability, OrganizationScopeType } from "@/types/organization-scope"
import { resolveOrganizationCapability } from "@/utils/organizationCapabilities"
import { resolveRoleProfile } from "@/utils/roleProfile"

type PermissionRouteLike = {
  meta?: Record<string, unknown>
}

export interface RouteAccessContext {
  hqMenuEnabled?: boolean
  activeScopeType?: OrganizationScopeType
  capabilities?: OrganizationCapability[]
}

const hasPermission = (permissionList: string[], route: PermissionRouteLike) => {
  const permissions = route.meta?.permissions
  if (Array.isArray(permissions)) {
    return permissions.some(permission => typeof permission === "string" && permissionList.includes(permission))
  }
  return true
}

const hasRole = (roles: RoleType[], route: PermissionRouteLike) => {
  const routeRoles = route.meta?.roles
  if (Array.isArray(routeRoles)) {
    return roles.some(role => routeRoles.includes(role.roleName))
  }
  return true
}

const hasRoleProfile = (roles: RoleType[], permissionList: string[], route: PermissionRouteLike) => {
  const hiddenRoleProfiles = route.meta?.hiddenRoleProfiles
  if (Array.isArray(hiddenRoleProfiles)) {
    const profile = resolveRoleProfile(permissionList, roles)
    return !hiddenRoleProfiles.includes(profile.code)
  }
  return true
}

const hasViewScopeAccess = (route: PermissionRouteLike, context: RouteAccessContext = {}) => {
  const scopeTypes = route.meta?.scopeTypes
  if (Array.isArray(scopeTypes) && scopeTypes.length > 0) {
    return Boolean(context.activeScopeType && scopeTypes.includes(context.activeScopeType))
  }
  if (context.hqMenuEnabled === true) {
    return route.meta?.hqOnly === true
  }
  if (route.meta?.hqOnly === true) {
    return false
  }
  return true
}

export const hasAccess = (
  route: PermissionRouteLike,
  roles: RoleType[],
  permissionList: string[],
  context: RouteAccessContext = {},
) => {
  return hasRole(roles, route)
    && hasRoleProfile(roles, permissionList, route)
    && hasPermission(permissionList, route)
    && hasViewScopeAccess(route, context)
}

const normalizeRoutePath = (path: string) => {
  if (!path || path === "/") return "/"
  const normalized = path.startsWith("/") ? path : `/${path}`
  return normalized.replace(/\/+$/, "") || "/"
}

const resolveChildPath = (parentPath: string, routePath: string) => {
  if (!routePath) return normalizeRoutePath(parentPath)
  if (routePath.startsWith("/")) return normalizeRoutePath(routePath)
  return normalizeRoutePath(`${normalizeRoutePath(parentPath)}/${routePath}`)
}

const isCatchAllRoutePath = (path: string) => {
  return path.includes(":pathMatch")
}

export const routePathMatches = (routePath: string, targetPath: string) => {
  if (isCatchAllRoutePath(routePath)) return false
  const normalizedRoutePath = normalizeRoutePath(routePath)
  const normalizedTargetPath = normalizeRoutePath(targetPath)
  if (normalizedRoutePath === normalizedTargetPath) return true

  const pattern = normalizedRoutePath
    .split("/")
    .map((segment) => {
      if (!segment) return ""
      if (segment.startsWith(":")) return "[^/]+"
      return segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
    })
    .join("/")
  return new RegExp(`^${pattern}$`).test(normalizedTargetPath)
}

export const flattenRoutesWithFullPath = (routes: RouteRecordRaw[], parentPath = ""): Array<RouteRecordRaw & { fullPath: string }> => {
  return routes.reduce<Array<RouteRecordRaw & { fullPath: string }>>((acc, route) => {
    const fullPath = resolveChildPath(parentPath, route.path)
    acc.push({ ...route, fullPath })
    if (route.children) {
      acc.push(...flattenRoutesWithFullPath(route.children, fullPath))
    }
    return acc
  }, [])
}

const hasRoutePath = (routes: RouteRecordRaw[], targetPath: string, parentPath = ""): boolean => {
  return flattenRoutesWithFullPath(routes, parentPath).some(route => routePathMatches(route.fullPath, targetPath))
}

const getFirstChildPath = (routes: RouteRecordRaw[], parentPath = "") => {
  return flattenRoutesWithFullPath(routes, parentPath).find(route => !isCatchAllRoutePath(route.fullPath))?.fullPath
}

const syncRouteListProps = (routes: RouteRecordRaw[]) => {
  routes.forEach((route) => {
    if (route.props && typeof route.props === "object" && "routeList" in route.props) {
      route.props = {
        ...route.props,
        routeList: routes,
      }
    }
  })
}

export const filterAuthorizedRoutes = (
  routes: RouteRecordRaw[],
  roles: RoleType[],
  permissionList: string[],
  parentPath = "",
  context: RouteAccessContext = {},
) => {
  const res: RouteRecordRaw[] = []
  routes.forEach((route) => {
    const hadAccess = hasAccess(route, roles, permissionList, context)
    if (!hadAccess) return

    const r = {
      ...route,
      meta: route.meta ? { ...route.meta } : route.meta,
      props: route.props && typeof route.props === "object" ? { ...route.props } : route.props,
    } as RouteRecordRaw
    const fullPath = resolveChildPath(parentPath, route.path)
    const hasChildren = Array.isArray(route.children) && route.children.length > 0
    if (hasChildren) {
      r.children = filterAuthorizedRoutes(route.children as RouteRecordRaw[], roles, permissionList, fullPath, context)
      if (typeof r.redirect === "string" && !hasRoutePath(r.children, r.redirect, fullPath)) {
        r.redirect = getFirstChildPath(r.children, fullPath) ?? r.redirect
      }
    }
    if (!hasChildren || (r.children?.length ?? 0) > 0) {
      res.push(r)
    }
  })
  syncRouteListProps(res)
  return res
}

const isCapabilityLocked = (route: PermissionRouteLike, context: RouteAccessContext) => {
  const code = route.meta?.capabilityCode
  if (typeof code !== "string") return false
  return resolveOrganizationCapability(context.capabilities, code).state === "authorized-module-not-active"
}

/**
 * 侧边栏可额外展示“已授权但模块未开通”的占位项，不会把它注册为可执行路由。
 */
export const filterNavigationRoutes = (
  routes: RouteRecordRaw[],
  roles: RoleType[],
  permissionList: string[],
  parentPath = "",
  context: RouteAccessContext = {},
) => {
  const res: RouteRecordRaw[] = []
  routes.forEach((route) => {
    const accessible = hasAccess(route, roles, permissionList, context)
    const locked = !accessible
      && hasRole(roles, route)
      && hasRoleProfile(roles, permissionList, route)
      && hasViewScopeAccess(route, context)
      && isCapabilityLocked(route, context)
    if (!accessible && !locked) return

    const fullPath = resolveChildPath(parentPath, route.path)
    const clone = {
      ...route,
      meta: route.meta ? { ...route.meta } : route.meta,
      props: route.props && typeof route.props === "object" ? { ...route.props } : route.props,
    } as RouteRecordRaw
    if (locked) {
      const code = String(route.meta?.capabilityCode)
      const capability = resolveOrganizationCapability(context.capabilities, code).capability
      clone.meta = {
        ...clone.meta,
        alwaysShow: false,
        locked: true,
        lockedReason: capability?.reason || `${capability?.requiredModule || "所需模块"}未开通`,
      }
      delete clone.children
      delete clone.redirect
      res.push(clone)
      return
    }

    if (route.children?.length) {
      clone.children = filterNavigationRoutes(route.children, roles, permissionList, fullPath, context)
      if (typeof clone.redirect === "string" && !hasRoutePath(clone.children, clone.redirect, fullPath)) {
        clone.redirect = getFirstChildPath(clone.children, fullPath) ?? clone.redirect
      }
    }
    if (!route.children?.length || (clone.children?.length ?? 0) > 0) res.push(clone)
  })
  syncRouteListProps(res)
  return res
}
